import React, { useState } from 'react';
import { ShieldCheck, KeyRound } from 'lucide-react';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import { STEP, stepIcon } from './toolkit/ToolkitFileStep';
import ToolkitWriteConfirm, { confirmed } from './toolkit/ToolkitWriteConfirm';
import useOnPrem, { rowsOf } from './toolkit/useOnPrem';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › HConnect OXI — the desktop's Scripts › OXI, for the property's OPERA v5:
//
//   Check   — whether the HConnect OXI interface is installed: licence, communication details, business events,
//             parameters, alert and interface setup (toolkit onprem.js hconnect-oxi-status; a read).
//   Licence — activates the HConnect OXI licence row, or creates it (WRITES.hconnect-oxi-licence; a write, confirmed).
//
// NOT HERE: the desktop's full interface install. It ran a PL/SQL template (docs/hconnect-oxi-install.md) that is not
// in the desktop repository, so there is nothing to port; once the script is supplied it becomes a write like these.

function ToolkitHconnectOxiPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready, project } = toolkit;
    const { ask, write } = useOnPrem(ctx);
    const [check, setCheck] = useState({ state: STEP.IDLE, message: '', rows: null });
    const [lic, setLic] = useState({ state: STEP.IDLE, message: '' });
    const [confirm, setConfirm] = useState('');

    const runCheck = async () => {
        setCheck({ state: STEP.BUSY, message: 'Asking SyncConnect…', rows: null });
        try {
            const d = await ask('hconnect-oxi-status');
            if (d.status !== 'RETURNED') { setCheck({ state: STEP.ERROR, message: d.error_message || `No answer yet (${d.status}).`, rows: null }); return; }
            const rows = rowsOf(d.result);
            const missing = rows.filter((r) => Number(r.found || 0) === 0).map((r) => r.check_name);
            setCheck({ state: missing.length ? STEP.ERROR : STEP.DONE, rows,
                message: missing.length ? `Missing: ${missing.join(', ')}` : 'Every part of the HConnect OXI interface is present.' });
        } catch (e) {
            setCheck({ state: STEP.ERROR, message: e?.response?.data?.message || e.message, rows: null });
        }
    };

    const installLicence = async () => {
        setLic({ state: STEP.BUSY, message: 'Sent to SyncConnect — waiting for it to run…' });
        try {
            const d = await write('hconnect-oxi-licence', {}, confirm);
            if (d.status === 'ERROR') { setLic({ state: STEP.ERROR, message: d.error_message }); return; }
            if (d.status !== 'RETURNED') { setLic({ state: STEP.ERROR, message: `No answer yet (${d.status}) — run the check before trying again.` }); return; }
            const o = d.writeOutcome || {};
            setLic({ state: o.status === 'success' ? STEP.DONE : STEP.ERROR, message: o.message });
            setConfirm('');
        } catch (e) {
            setLic({ state: STEP.ERROR, message: e?.response?.data?.message || e.message });
        }
    };

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="HConnect OXI"
                subtitle="Check the HConnect OXI interface at the property, and install its licence." />

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">1</span> Installation check {stepIcon(check.state)}</div>
                <Button variant="secondary" onClick={runCheck} disabled={!ready || check.state === STEP.BUSY}><ShieldCheck className="icon" /> Check</Button>
                {check.message && <p className={`tkb-msg${check.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{check.message}</p>}
                {check.rows && (
                    <div className="otx-table-wrap" style={{ marginTop: '0.6rem', maxWidth: 520 }}>
                        <table className="otx-table">
                            <thead><tr><th>Part</th><th>Found</th></tr></thead>
                            <tbody>{check.rows.map((r) => (
                                <tr key={r.check_name}><td>{r.check_name}</td>
                                    <td className={Number(r.found || 0) ? 'otx-ok' : 'otx-fail'}>{r.found}</td></tr>
                            ))}</tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">2</span> Licence {stepIcon(lic.state)}</div>
                <ToolkitWriteConfirm project={project} value={confirm} onChange={setConfirm}
                    what="Activates the HCONNECT licence row in application_and_licenses for this property, or creates it if there is none." />
                <Button variant="success" onClick={installLicence} disabled={!ready || !confirmed(project, confirm) || lic.state === STEP.BUSY} style={{ marginTop: '0.6rem' }}>
                    <KeyRound className="icon" /> Install licence
                </Button>
                {lic.message && <p className={`tkb-msg${lic.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{lic.message}</p>}
                <p className="tkb-hint">The full interface install (the desktop's install script) is not available yet — its script is not in the desktop repository.</p>
            </div>
        </div>
    );
}

export default ToolkitHconnectOxiPage;
